import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useMultiAirlineCampaignPerformanceQuery } from "../../../hooks/useCampaignPerformanceQuery";
import { useSelectedAirline } from "../../../hooks/useSelectedAirline";
import { useUserAirlineGroupsSuspenseQuery } from "../../../hooks/useUserAirlineGroupsQuery";
import { ChartNoData } from "../ChartNoData";
import { SearchCoverageChart, type SearchCoverageAirlineSeries } from "./SearchCoverageChart";

export type MultiSeriesSearchCoverageLoaderProps = {
  from: Date;
  to: Date;
};

export const MultiSeriesSearchCoverageLoader = ({ from, to }: MultiSeriesSearchCoverageLoaderProps) => {
  const i18n = useI18n();
  const { selectedAirline, selectedAirlineCodes } = useSelectedAirline({ useSuspense: true });
  const { data: userAirlineGroups } = useUserAirlineGroupsSuspenseQuery();

  const airlineMetrics = useMultiAirlineCampaignPerformanceQuery({
    from,
    to,
    metrics: ["SEARCH_COVERAGE_PERCENTAGE"],
    airlineCodes: selectedAirlineCodes,
  });

  const series: SearchCoverageAirlineSeries[] = useMemo(() => {
    const airlineNameByCode = new Map<string, string>(
      userAirlineGroups.flatMap(({ airlines }) => airlines.map(({ code, name }) => [code, name] as const))
    );
    if (selectedAirline) {
      airlineNameByCode.set(selectedAirline.code, selectedAirline.name);
    }

    return airlineMetrics.flatMap(({ airlineCode, metrics }) => {
      const metric = metrics.find((m) => m.metadata.metricName === "SEARCH_COVERAGE_PERCENTAGE");
      return metric ? [{ airlineCode, airlineName: airlineNameByCode.get(airlineCode) ?? airlineCode, metric }] : [];
    });
  }, [airlineMetrics, userAirlineGroups, selectedAirline]);

  if (series.length === 0) {
    return <ChartNoData titleText={i18n.trans(t("flights_airline_portal_realtime_performance_metric_three_name"))} />;
  }

  return <SearchCoverageChart series={series} showAirlineLabel />;
};
